import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { companyEditConfirmPresentation } from '@/features/company/constants'
import { AddUserModal } from '@/features/company/components/AddUserModal'
import { SendEmailModal } from '@/features/company/components/SendEmailModal'
import { CommunicationLogModal } from '@/features/company/components/CommunicationLogModal'
import { PropertyStaffTransferModal } from './PropertyStaffTransferModal'
import type { PropertyContactsDialogsProps } from '../types'

export function PropertyContactsDialogs({
  property,
  propertyId,
  companyId,
  selectedUserIds,
  transferOpen,
  addUserOpen,
  logCallStaff,
  composeConfirmOpen,
  emailBroadcastOpen,
  removeConfirm,
  deleteUserConfirm,
  deleteOrDisableConfirm,
  welcomeConfirm,
  onTransferClose,
  onTransferSuccess,
  onAddUserClose,
  onAddUserSuccess,
  onLogCallClose,
  onComposeConfirmed,
  onComposeDismiss,
  onEmailClose,
  onEmailSuccess,
  onRemoveRole,
  onRemoveRoleDismiss,
  onDeleteUserChoice,
  onDeleteOrDisable,
  onDeleteOrDisableDismiss,
  onWelcomeEmail,
  onWelcomeEmailDismiss,
}: PropertyContactsDialogsProps) {
  return (
    <>
      <PropertyStaffTransferModal
        isOpen={transferOpen}
        onClose={onTransferClose}
        propertyId={propertyId}
        property={property}
        onSuccess={onTransferSuccess}
      />

      {companyId != null && String(companyId).trim() !== '' && (
        <AddUserModal
          isOpen={addUserOpen}
          onClose={onAddUserClose}
          companyId={companyId}
          variant="propertyStaff"
          propertyId={propertyId}
          onSuccess={onAddUserSuccess}
        />
      )}

      <ConfirmDialog
        isOpen={composeConfirmOpen}
        title="Send Email"
        message={`Blast email will send to ${selectedUserIds.length} user(s). Continue?`}
        primaryLabel="Yes"
        onPrimary={onComposeConfirmed}
        secondaryLabel="No"
        onSecondary={onComposeDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-contacts-email-blast-confirm-overlay"
        primaryButtonDataTestId="property-contacts-email-blast-confirm-yes-button"
        secondaryButtonDataTestId="property-contacts-email-blast-confirm-no-button"
        closeButtonDataTestId="property-contacts-email-blast-confirm-close-button"
      />

      {emailBroadcastOpen && (
        <SendEmailModal
          selectedUserIds={selectedUserIds}
          propertyId={propertyId}
          onClose={onEmailClose}
          onSuccess={onEmailSuccess}
        />
      )}

      <CommunicationLogModal
        isOpen={!!logCallStaff}
        onClose={onLogCallClose}
        pmcType="PROPERTY"
        entityId={propertyId}
        staffDetail={logCallStaff}
        entityDetails={property ?? undefined}
      />

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title="Delete User Role?"
        message={
          removeConfirm.staff?.role_name === 'ROLE_REGIONAL_MANAGER'
            ? 'Regional Mgr is still connected to Properties. Do you want to remove ALL Property links?'
            : 'Are you sure you want to remove this user role?'
        }
        primaryLabel="Yes"
        onPrimary={onRemoveRole}
        secondaryLabel="No"
        onSecondary={onRemoveRoleDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-contacts-remove-role-confirm-overlay"
        primaryButtonDataTestId="property-contacts-remove-role-confirm-yes-button"
        secondaryButtonDataTestId="property-contacts-remove-role-confirm-no-button"
        closeButtonDataTestId="property-contacts-remove-role-confirm-close-button"
      />

      <ConfirmDialog
        isOpen={deleteUserConfirm.isOpen}
        title="Delete or Disable User?"
        message="There are no other roles for this User. Do you want to Delete or Disable the user? Disable, if you are moving a property to be managed by a different company, and you want to keep the user, but need to update their role. Otherwise, delete them to permanently remove them from the system."
        primaryLabel="Delete"
        onPrimary={() => onDeleteUserChoice(true)}
        secondaryLabel="Disable"
        onSecondary={() => onDeleteUserChoice(false)}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-contacts-delete-user-choice-overlay"
        primaryButtonDataTestId="property-contacts-delete-user-choice-delete-button"
        secondaryButtonDataTestId="property-contacts-delete-user-choice-disable-button"
        closeButtonDataTestId="property-contacts-delete-user-choice-close-button"
      />

      <ConfirmDialog
        isOpen={deleteOrDisableConfirm.isOpen}
        title={deleteOrDisableConfirm.type === 1 ? 'Delete User?' : 'Disable User?'}
        message={
          deleteOrDisableConfirm.type === 1
            ? 'Are you sure want to Delete User?'
            : 'Are you sure want to Disable User'
        }
        primaryLabel="Yes"
        onPrimary={onDeleteOrDisable}
        secondaryLabel="No"
        onSecondary={onDeleteOrDisableDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-contacts-delete-disable-final-overlay"
        primaryButtonDataTestId="property-contacts-delete-disable-final-yes-button"
        secondaryButtonDataTestId="property-contacts-delete-disable-final-no-button"
        closeButtonDataTestId="property-contacts-delete-disable-final-close-button"
      />

      <ConfirmDialog
        isOpen={welcomeConfirm.isOpen}
        title="Send Welcome Email"
        message="Do you want to send Welcome email to User?"
        primaryLabel="Yes"
        onPrimary={onWelcomeEmail}
        secondaryLabel="No"
        onSecondary={onWelcomeEmailDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-contacts-welcome-email-confirm-overlay"
        primaryButtonDataTestId="property-contacts-welcome-email-confirm-yes-button"
        secondaryButtonDataTestId="property-contacts-welcome-email-confirm-no-button"
        closeButtonDataTestId="property-contacts-welcome-email-confirm-close-button"
      />
    </>
  )
}
